// The table help of the casino (R2143, casino.md §9).
//
// Each table screen opens its own short help at its head: one HOW IT WORKS
// fold in the look of the menu fold (`overlay/howfold.ts`, R1895), with
// the facts of THAT game alone — how a bet is made, the house rules, the
// share the house keeps and the keys. The generic ECONOMY fold does not
// stand under the casino, because it explains the income and the auction.
//
// The fold is one part of its own (`gameview.ts`), the first of the
// screen, so a press that opens it rewrites the fold and nothing else.
// Its press carries `data-act="casino"` and `data-op="help"`, so the
// panel's own router takes it, and the panel keeps the open state per game
// in the browser store (`ui/localstore.ts`).
//
// Every figure comes from the hello catalog (R355) and every word from
// `gen/strings.ts`. The module is pure.

import { KEYS, type MapKeyId } from '../../gen/presentation';
import { CasinoGame, type CasinoRules } from '../../gen/proto/aquaphobia';
import { overlay as S } from '../../gen/strings';
import { groupThousands } from '../../locale/group';
import { arrowFace } from '../arrowpress';
import { type Fact, factsHtml } from '../cards/card';
import { esc } from '../esc';
import { stakeAt } from './casinostate';
import { part } from './gameview';
import { houseLine } from './lobby';
import { pct } from './pct';

/** The store key of the help fold of one game. */
export function helpKey(game: CasinoGame): string {
  return `ap.casino.help.${GAME_SLUG[game] ?? 'game'}`;
}

/** The word of each game in a store key. */
const GAME_SLUG: Partial<Record<CasinoGame, string>> = {
  [CasinoGame.CASINO_GAME_ROULETTE]: 'roulette',
  [CasinoGame.CASINO_GAME_BLACKJACK]: 'blackjack',
  [CasinoGame.CASINO_GAME_SLOTS]: 'slots',
  [CasinoGame.CASINO_GAME_HOLDEM]: 'holdem',
};

/** The cap of one casino key, as the KEYS panel prints it. */
function keyCap(id: MapKeyId): string {
  return KEYS.map[id].label;
}

/** The title of a control with the cap of its key: a reader who points
 *  at a control learns the key that presses it (R2143). */
export function keyTitle(name: string, id: MapKeyId): string {
  return ` title="${esc(S.casino_with_key({ name, key: keyCap(id) }))}"`;
}

const fact = (key: string, val: string): Fact => ({ key, val: esc(val) });

/** The KEYS fact: each key of the game with the word of its control. */
function keysFact(list: readonly [MapKeyId, string][]): Fact {
  return fact(S.casino_help_k_keys,
    list.map(([id, name]) => S.casino_help_key({ key: keyCap(id), name })).join(' · '));
}

/**
 * The facts of one game. `tier` is the stake tier of the table the reader
 * sits at, which states the blinds and the buy-in of hold'em.
 */
export function helpFacts(game: CasinoGame, rules: CasinoRules | null, tier: number): Fact[] {
  if (!rules) return [];
  const house = fact(S.casino_help_k_house, houseLine(game, rules));
  switch (game) {
    case CasinoGame.CASINO_GAME_ROULETTE:
      return [
        fact(S.casino_help_k_bet, S.roulette_help_bet),
        fact(S.casino_help_k_two, S.roulette_help_two),
        fact(S.casino_help_k_take, S.roulette_help_take),
        fact(S.casino_help_k_rebet, S.roulette_help_rebet),
        house,
        keysFact([
          ['casino_rebet', S.roulette_rebet],
          ['casino_take_back', S.roulette_take_back],
          ['casino_clear', S.roulette_clear],
        ]),
      ];
    case CasinoGame.CASINO_GAME_BLACKJACK:
      return [
        // A Spanish shoe holds no 10, and the help says so (R2752).
        fact(S.casino_help_k_shoe, (rules.blackjackSpanishShoe ? S.bj_shoe_spanish : S.bj_shoe)({
          decks: groupThousands(rules.blackjackDecks),
        })),
        fact(S.casino_help_k_dealer, S.bj_dealer_soft_17),
        fact(S.casino_help_k_natural, S.bj_natural_pays({
          num: groupThousands(rules.blackjackPaysNum), den: groupThousands(rules.blackjackPaysDen),
        })),
        fact(S.casino_help_k_insure, S.bj_insurance_pays({ pays: groupThousands(rules.insurancePays) })),
        house,
        keysFact([
          ['casino_bet', S.casino_place_bet],
          ['casino_hit', S.bj_hit],
          ['casino_stand', S.bj_stand],
          ['casino_double', S.bj_double],
        ]),
      ];
    case CasinoGame.CASINO_GAME_SLOTS:
      return [
        fact(S.casino_help_k_pull, S.slots_help_pull),
        fact(S.casino_help_k_payback, S.slots_rtp({ pct: pct(rules.slotsRtpPct) })),
        house,
        keysFact([['casino_spin', S.slots_spin]]),
      ];
    case CasinoGame.CASINO_GAME_HOLDEM: {
      const stake = stakeAt(rules, tier) ?? rules.stakes[0];
      const big = stake?.minBet ?? 0;
      const rows: Fact[] = [];
      if (stake) {
        rows.push(fact(S.casino_help_k_blinds, S.poker_blinds({
          small: groupThousands(stake.smallBlind), big: groupThousands(big),
        })));
        rows.push(fact(S.casino_help_k_buy_in, S.casino_buy_in({
          min: groupThousands(stake.buyInMin), max: groupThousands(stake.buyInMax),
        })));
      }
      rows.push(fact(S.casino_help_k_rake, S.poker_rake({
        pct: pct(rules.rakePct * 100), cap: groupThousands(rules.rakeCapBb * big),
      })));
      rows.push(fact(S.casino_help_k_top_up, S.poker_top_up_when));
      rows.push(keysFact([
        ['casino_fold', S.poker_fold],
        ['casino_call', S.poker_call],
        ['casino_bet', S.poker_bet],
      ]));
      return rows;
    }
    default:
      return [];
  }
}

/**
 * The help fold of one table screen, as the part `help`: the row (the
 * arrow, HOW IT WORKS, the count) and, while open, the fact rows. A game
 * with no fact draws an empty part, so the shape of the screen holds.
 */
export function helpPart(game: CasinoGame, facts: readonly Fact[], open: boolean): string {
  if (facts.length === 0) return part('help', 'class="casino-help"', '');
  return part('help', `class="casino-help hw${open ? ' open' : ''}"`,
    `<button class="hw-row" data-act="casino" data-op="help" data-game="${game}" aria-expanded="${open}">`
    + `${arrowFace(open ? 'down' : 'right')}<b>${S.how_head}</b>`
    + `<i class="hw-n">${S.how_count({ n: facts.length })}</i></button>`
    + (open ? factsHtml(facts, 'hw-facts') : ''));
}
